import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import TreeMapChart from "../../../../../components/chart/TreeMapChart";
import TextPillCard from "../components/TextPillCard";
import ReactTable from "../../../../../lib/table/react-table";
import IndiaMap from "../../../../../components/chart/IndiaMap";
import {
  useGetAllProjectEventsList,
  useGetLocationDistribution,
  useGetLocationReport,
  useGetMajorCities,
  useGetSummaryContentInfo,
  useGetZonalDistribution,
} from "../../../../../queries/query-hooks/workspace/events-hook";
import { useParams } from "react-router-dom";
import { transformIndiaMapData } from "../utils/utils";
import MapAnalyticsSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/map-analytics-skeleton";
import SquareChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/square-chart-skeleton";
import CommonTableLoader from "../../../../../skeleton/common-table-loader";
import { downloadTablePNG } from "../../../../../common/utils/utils";
import useCsvDownloader from "../../../../../components/custom-hooks/use-csv-downloader";
import { ProjectHeaderTopBar, TableHeader, CardNotFound } from "@ui";
import ProjectFilters from "../components/ProjectFilters";
import NavSwiperSkeleton from "skeleton/workspace/clirnet/brand-ads/nav-swiper-skeleton";
import HeaderPillFilters from "../components/HeaderPillFilters";

const LOCATION_REPORT_TABLE_COLUMNS = [
  {
    columnId: 1,
    Header: "State",
    accessor: "state",
    disableFilters: true,
    Cell: (value) => {
      if (!value.value) return "---";
      return value.value;
    },
  },
  {
    columnId: 2,
    Header: "Registration",
    accessor: "registration_count",
    disableFilters: true,
  },
  {
    columnId: 3,
    Header: "Participations",
    accessor: "participant_count",
    disableFilters: true,
  },
  {
    columnId: 4,
    Header: "Conversion (in %)",
    accessor: "covertion_rate",
    disableFilters: true,
    Cell: (value) => <div>{value.value}%</div>,
  },
];

const LocationInsights = () => {
  const { project_id } = useParams();
  const { data: summaryContentInfo, isLoading: isSummaryContentInfoLoading } =
    useGetSummaryContentInfo(project_id);
  const [filterBy, setFilterBy] = useState("Registrations");
  const [activeFilter, setActiveFilter] = useState({
    id: 0,
    index: 0,
  });
  const [eventFilterPayload, setEventFilterPayload] = useState({
    type: "all",
  });
  const locationTableRef = useRef(null);
  const { downloadCSV } = useCsvDownloader();
  const { data: allProjectList, isLoading: isAllProjectListLoading } =
    useGetAllProjectEventsList(project_id);

  const [updatedAllProjectList, setUpdatedAllProjectList] = useState([]);
  useEffect(() => {
    if (allProjectList?.event_list?.length > 0) {
      const updatedAllProjectListValue = [
        { event_name: "All", event_id: 0 },
        ...allProjectList?.event_list,
      ];

      setUpdatedAllProjectList(updatedAllProjectListValue);
    }
  }, [allProjectList]);

  useEffect(() => {
    if (activeFilter?.id === 0) {
      setEventFilterPayload({
        type: "all",
      });
    } else {
      setEventFilterPayload({
        event_id: activeFilter?.id,
      });
    }
  }, [activeFilter]);

  const {
    data: locationDistribution,
    isLoading: isLocationDistributionLoading,
  } = useGetLocationDistribution(project_id, {
    ...eventFilterPayload,
    analysis_type: filterBy?.slice(0, -1)?.toLowerCase() || "registration", // participation || registration
  });

  const { data: zonalDistribution, isLoading: isZonalDistributionLoading } =
    useGetZonalDistribution(project_id, {
      ...eventFilterPayload,
      analysis_type: filterBy?.slice(0, -1)?.toLowerCase() || "registration", // participation || registration
    });

  const { data: majorCities, isLoading: isMajorCitiesLoading } =
    useGetMajorCities(project_id, {
      ...eventFilterPayload,
      analysis_type: filterBy?.slice(0, -1)?.toLowerCase() || "registration", // participation || registration
    });

  const { data: locationReport, isLoading: isLocationReportLoading } =
    useGetLocationReport(project_id);

  const locationTableCsvFormat = {
    header: ["State", "Registration", "Participations", "Conversion (in %)"],
    column: [
      "state",
      "registration_count",
      "participant_count",
      "covertion_rate",
    ],
    rows: locationReport,
  };

  const handleTableCsvDownload = () => {
    downloadCSV(locationTableCsvFormat);
  };

  const updateFilterHandler = (value) => {
    setFilterBy(value);
  };

  function formatZonalDistibutionData(inputArray = []) {
    if (!Array.isArray(inputArray)) return [];
    return inputArray.map(({ level, percentage }) => ({
      x: level || "Other",
      y: percentage,
    }));
  }

  function majorCitiesFormatter(obj = {}) {
    return Object.keys(obj).map((key) => ({ name: key }));
  }

  const indiaMapFormattedData = transformIndiaMapData(locationDistribution);

  const zonalDistributionChartOptions =
    formatZonalDistibutionData(zonalDistribution);

  const majorCitiesFormattedData = majorCitiesFormatter(majorCities);

  const downloadTablePNGFunc = () => {
    downloadTablePNG(locationTableRef);
  };

  return (
    <div>
      <div className="mb-3 mb-md-0">
        <ProjectHeaderTopBar
          title="Location Insights"
          projectType="Events"
          filterType="all"
          projectStartDate={
            summaryContentInfo?.project_detail?.project_start_date || ""
          }
          projectEndDate={
            summaryContentInfo?.project_detail?.project_end_date || ""
          }
          isProjectDetailsLoading={isSummaryContentInfoLoading}
          needProjectStatus={false}
          rightComponent={
            <ProjectFilters
              filterType="all"
              updateFilterHandler={updateFilterHandler}
              filterBy={filterBy}
            />
          }
        />
      </div>

      <div className="col-sm-12">
        {!isAllProjectListLoading ? (
          <HeaderPillFilters
            titles={updatedAllProjectList}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
          />
        ) : (
          <NavSwiperSkeleton />
        )}
      </div>

      <div className="d-flex flex-column gap-4">
        <Row>
          <Col md={6} sm={12} className="d-flex flex-column mb-4 mb-md-0">
            {!isLocationDistributionLoading &&
            locationDistribution?.length > 0 ? (
              <IndiaMap
                title={`${filterBy} by Location`}
                stateData={indiaMapFormattedData}
              />
            ) : isLocationDistributionLoading ? (
              <MapAnalyticsSkeleton />
            ) : (
              <CardNotFound
                title={`${filterBy} by Location`}
                isTitleCenter={true}
              />
            )}
          </Col>
          <Col md={6} sm={12} className="d-flex flex-column gap-3">
            <Col md={12}>
              {!isMajorCitiesLoading && Object.keys(majorCities)?.length > 0 ? (
                <TextPillCard
                  data={majorCitiesFormattedData}
                  title={"Major Cities"}
                />
              ) : isMajorCitiesLoading ? (
                <SquareChartSkeleton />
              ) : (
                <CardNotFound
                  title={"Major Cities"}
                  isTitleCenter={true}
                  height={350}
                />
              )}
            </Col>
            <Col md={12}>
              {!isZonalDistributionLoading && zonalDistribution?.length > 0 ? (
                <TreeMapChart
                  title={`${filterBy} by Zone`}
                  series={zonalDistributionChartOptions}
                  height={350}
                />
              ) : isZonalDistributionLoading ? (
                <SquareChartSkeleton height={350} />
              ) : (
                <CardNotFound
                  title={`${filterBy} by Zone`}
                  isTitleCenter={true}
                  height={350}
                />
              )}
            </Col>
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            <Card className="rounded-3">
              <TableHeader
                title="Location"
                downloadProps={{
                  needCsvDownload: true,
                  needImgDownload: true,
                  exportCSVHandler: handleTableCsvDownload,
                  downloadImageHandler: downloadTablePNGFunc,
                }}
              />
              <Card.Body>
                <div ref={locationTableRef}>
                  {!isLocationReportLoading ? (
                    <ReactTable
                      data={locationReport}
                      columns={LOCATION_REPORT_TABLE_COLUMNS}
                      isPaginationShow={false}
                      bordered
                      noDataText="No Data Available"
                    />
                  ) : (
                    <CommonTableLoader
                      tableRowLength={5}
                      hasSearch={false}
                      hasActions={false}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LocationInsights;
